import type { LucideIcon } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { StepConnector, StepContent, StepNodeWithTooltip } from "./step-progress-bar.parts";

export type StepProgressState = "done" | "current" | "upcoming";
export type StepProgressShape = "circle" | "rounded";
export type StepProgressSize = "default" | "compact";
export type StepProgressOrientation = "horizontal" | "vertical";
export type StepProgressSurface = "subtle" | "transparent";
export type StepProgressContentMode = "icon-only" | "descriptive";
export type StepProgressNodeContentMode = "icon" | "index";

export interface StepProgressItem {
  id: string;
  label: string;
  detail: string;
  state: StepProgressState;
  icon: LucideIcon;
  stepNumber?: number;
  title?: string;
  description?: string;
}

interface Props {
  steps: ReadonlyArray<StepProgressItem>;
  ariaLabel: string;
  className?: string;
  shape?: StepProgressShape;
  size?: StepProgressSize;
  orientation?: StepProgressOrientation;
  surface?: StepProgressSurface;
  contentMode?: StepProgressContentMode;
  nodeContentMode?: StepProgressNodeContentMode;
  showTooltip?: boolean;
}

export function StepProgressBar({
  steps,
  ariaLabel,
  className,
  shape = "circle",
  size = "default",
  orientation = "horizontal",
  surface = "transparent",
  contentMode = "icon-only",
  nodeContentMode = "icon",
  showTooltip = true,
}: Props) {
  return (
    <ol
      className={cn(
        "min-w-0",
        orientation === "horizontal"
          ? "inline-flex items-center"
          : "inline-flex flex-col items-start gap-0",
        surface === "subtle" && orientation === "horizontal"
          ? "rounded-full border border-border/70 bg-surface-subtle px-2 py-1"
          : null,
        surface === "subtle" && orientation === "vertical"
          ? "rounded-xl border border-border/70 bg-card p-4"
          : null,
        className,
      )}
      aria-label={ariaLabel}
    >
      {steps.map((step, index) => {
        const next = steps[index + 1];
        const node = (
          <StepNodeWithTooltip
            step={step}
            shape={shape}
            size={size}
            nodeContentMode={nodeContentMode}
            stepNumber={step.stepNumber ?? index + 1}
            showTooltip={showTooltip}
          />
        );
        return (
          <li
            key={step.id}
            aria-current={step.state === "current" ? "step" : undefined}
            className={cn(
              "min-w-0",
              orientation === "horizontal"
                ? contentMode === "descriptive"
                  ? "flex min-w-0 items-center"
                  : "flex items-center"
                : "grid grid-cols-[auto_1fr] gap-x-3",
            )}
          >
            {orientation === "horizontal" && contentMode === "descriptive" ? (
              <div className="flex min-w-0 items-center">
                {node}
                <StepContent step={step} orientation={orientation} size={size} />
                {next ? (
                  <StepConnector
                    currentState={step.state}
                    nextState={next.state}
                    orientation={orientation}
                    size={size}
                  />
                ) : null}
              </div>
            ) : null}
            {orientation === "vertical" || contentMode === "icon-only" ? (
              <>
                <div
                  className={cn(
                    orientation === "horizontal"
                      ? "flex items-center"
                      : "flex flex-col items-center",
                  )}
                >
                  {node}
                  {next ? (
                    <StepConnector
                      currentState={step.state}
                      nextState={next.state}
                      orientation={orientation}
                      size={size}
                    />
                  ) : null}
                </div>
                {contentMode === "descriptive" ? (
                  <StepContent step={step} orientation={orientation} size={size} />
                ) : null}
              </>
            ) : null}
            <span className="sr-only">{`${step.label}: ${step.detail}`}</span>
          </li>
        );
      })}
    </ol>
  );
}
